import { useState } from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Card, CardContent } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Separator } from "../../../../components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../../components/ui/collapsible";
import MarkdownRenderer from "../../../../components/agno/MarkdownRenderer";

type Props = {
  records: any[];
  fieldDefinitions: any[];
};

const KEY_FIELDS_COUNT = 4;

function isMarkdownLike(value: string): boolean {
  return /[#*_`[\]|>-]{2}/.test(value) || value.includes("\n");
}

const formatFieldValue = (value: any, fieldDef?: any) => {
  if (value === null || value === undefined) return <span className="text-muted-foreground">--</span>;

  if (typeof value === "string" && (value.startsWith("http://") || value.startsWith("https://"))) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline inline-flex items-center gap-1 text-[13px]"
      >
        <span className="truncate max-w-[200px]">{value}</span>
        <ExternalLink size={12} />
      </a>
    );
  }

  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;

  if (fieldDef?.data_type === "datetime" && value) {
    try {
      return <span>{format(new Date(value), "MMM dd, yyyy HH:mm")}</span>;
    } catch {
      return <span>{String(value)}</span>;
    }
  }

  if (Array.isArray(value)) return <span>{value.join(", ")}</span>;
  if (typeof value === "object") return <span>{JSON.stringify(value)}</span>;

  const str = String(value);
  if (isMarkdownLike(str)) {
    return (
      <div className="text-[13px]">
        <MarkdownRenderer>{str.slice(0, 300)}</MarkdownRenderer>
      </div>
    );
  }

  return <span>{str}</span>;
};

const CustomObjectCardView = ({ records, fieldDefinitions }: Props) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const displayFields = fieldDefinitions
    .filter((f: any) => !f.exclude_display)
    .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));

  if (records.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-muted-foreground">No records found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {records.map((record: any) => {
        const recordId = record.id || record.external_id || Math.random().toString();
        const isExpanded = expandedIds.has(recordId);
        const keyFields = displayFields.slice(0, KEY_FIELDS_COUNT);
        const remainingFields = displayFields.slice(KEY_FIELDS_COUNT);
        const title = record.name || record.title || record.subject || record.display_name || recordId;

        return (
          <Card key={recordId} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[13px] font-semibold text-foreground">{title}</p>
                {record.status && (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">
                    {record.status}
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {keyFields.map((field: any) => {
                  const fieldKey = field.column_name || field.key;
                  const value = record[fieldKey] ?? record[field.display_name];
                  return (
                    <div key={fieldKey} className="flex flex-col">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {field.display_name || fieldKey}
                      </p>
                      <div className="text-[13px] text-foreground">
                        {formatFieldValue(value, field)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {remainingFields.length > 0 && (
                <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(recordId)}>
                  <CollapsibleContent>
                    <Separator className="my-3" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {remainingFields.map((field: any) => {
                        const fieldKey = field.column_name || field.key;
                        const value = record[fieldKey] ?? record[field.display_name];
                        return (
                          <div key={fieldKey} className="flex flex-col">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              {field.display_name || fieldKey}
                            </p>
                            <div className="text-[13px] text-foreground">
                              {formatFieldValue(value, field)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                  <div className="mt-2 flex justify-center">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                </Collapsible>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default CustomObjectCardView;
